import React, { useState, useEffect, useMemo } from "react";
import { useTableColumnFilters, FilterTh } from "../components/tableFilters";
import { useNavigate } from "react-router-dom";
import { CostCategory, CostItem } from "../types";
import { costService } from "../services/dataService";
import { formatCurrency, turkishIncludes } from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./PurchaseOrders.css";
import "./CostManagement.css";
import { usePermissions } from "../context/PermissionContext";

/**
 * Maliyet Yönetimi — maliyet kategorileri ve kalemleri (birim fiyat, döviz, fire, tedarikçi).
 * Ortak po-* dili (PurchaseOrders.css) + bu ekrana özgü cm-* ekleri (CostManagement.css).
 * Yeni kategori/kalem ve kalem detayı ayrı ekranlarda (CategoryForm / CostItemForm).
 */

interface CostManagementProps {}

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  chev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 8v6l-4-2v-4L3 5Z" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  arrow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>,
};

/** Esc yalnız bu modalı kapatsın (capture + stopPropagation). */
const useEscClose = (onClose: () => void) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);
};

const CostManagement: React.FC<CostManagementProps> = () => {
  const { has } = usePermissions(); // yetki matrisi
  const [activeTab, setActiveTab] = useState<"categories" | "items">(
    "categories",
  );
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [costItems, setCostItems] = useState<CostItem[]>([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] =
    useState<string>("");
  const [itemSearch, setItemSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CostItem | null>(null);
  const [editCategoryModalOpen, setEditCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CostCategory | null>(
    null,
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab === "categories") {
      loadCategories();
      loadCostItems(); // Kategoriler için de maliyet kalemlerini yükle
    } else if (activeTab === "items") {
      loadCostItems();
      loadCategories(); // Kategori filtresi için kategorileri de yükle
    }
  }, [activeTab]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await costService.getCostCategories();
      setCategories(data);
    } catch (error) {
      console.error("❌ Failed to load categories:", error);
      setAlertModal({
        isOpen: true,
        message: "Kategoriler yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  };

  const loadCostItems = async () => {
    try {
      setLoading(true);
      const data = await costService.getCostItems();
      setCostItems(data);
    } catch (error) {
      console.error("❌ Failed to load cost items:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemleri yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  // Kategori adını bul
  const getCategoryName = (categoryId: string): string => {
    const category = categories.find((c) => c.costCategoryId === categoryId);
    return category?.categoryName || "-";
  };

  const handleEditItem = (item: CostItem) => {
    setEditingItem(item);
    setEditModalOpen(true);
  };

  const handleSaveEdit = async (updatedItem: CostItem) => {
    if (!editingItem) return;

    try {
      setLoading(true);
      await costService.updateCostItem(editingItem.costItemId, updatedItem);
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemi başarıyla güncellendi!",
        type: "success",
      });
      setEditModalOpen(false);
      setEditingItem(null);
      loadCostItems();
    } catch (error) {
      console.error("Failed to update cost item:", error);
      setAlertModal({
        isOpen: true,
        message: "Maliyet kalemi güncellenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category: CostCategory) => {
    setEditingCategory(category);
    setEditCategoryModalOpen(true);
  };

  const handleSaveCategoryEdit = async (updatedCategory: CostCategory) => {
    if (!editingCategory) return;

    try {
      setLoading(true);
      await costService.updateCostCategory(
        editingCategory.costCategoryId,
        updatedCategory,
      );
      setAlertModal({
        isOpen: true,
        message: "Kategori başarıyla güncellendi!",
        type: "success",
      });
      setEditCategoryModalOpen(false);
      setEditingCategory(null);
      loadCategories();
    } catch (error) {
      console.error("Failed to update category:", error);
      setAlertModal({
        isOpen: true,
        message: "Kategori güncellenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (
    categoryId: string,
    categoryName: string,
  ) => {
    setConfirmModal({
      isOpen: true,
      message: `"${categoryName}" kategorisini silmek istediğinize emin misiniz?\n\nBu kategoriye ait maliyet kalemleri de silinecektir!`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await costService.deleteCostCategory(categoryId);
          setAlertModal({
            isOpen: true,
            message: "Kategori başarıyla silindi!",
            type: "success",
          });
          loadCategories();
          loadCostItems();
        } catch (error) {
          console.error("Failed to delete category:", error);
          setAlertModal({
            isOpen: true,
            message: "Kategori silinemedi! Bu kategori kullanımda olabilir.",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Maliyet kalemi silme helper fonksiyonu
  const handleDeleteCostItem = (itemId: string, itemName: string) => {
    setConfirmModal({
      isOpen: true,
      message: `"${itemName}" maliyet kalemini silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!`,
      type: "danger",
      onConfirm: async () => {
        try {
          await costService.deleteCostItem(itemId);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kalemi başarıyla silindi!",
            type: "success",
          });
          loadCostItems();
        } catch (error) {
          console.error("Failed to delete cost item:", error);
          setAlertModal({
            isOpen: true,
            message: "Maliyet kalemi silinemedi! Bu kalem kullanımda olabilir.",
            type: "error",
          });
        }
      },
    });
  };

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getCategoryItems = (categoryId: string) => {
    return costItems.filter((item) => item.costCategoryId === categoryId);
  };

  const cmBaseItems = useMemo(
    () =>
      costItems.filter((item) => {
        if (selectedCategoryFilter && item.costCategoryId !== selectedCategoryFilter) return false;
        if (itemSearch) {
          return (
            turkishIncludes(item.itemName || "", itemSearch) ||
            turkishIncludes(item.description || "", itemSearch) ||
            turkishIncludes(item.supplier || "", itemSearch)
          );
        }
        return true;
      }),
    [costItems, selectedCategoryFilter, itemSearch],
  );
  const cmItemDefs = useMemo(
    () => [
      { key: "name", value: (i: any) => i.itemName || "" },
      { key: "category", value: (i: any) => i.costCategory?.categoryName || getCategoryName(i.costCategoryId) },
      { key: "price", type: "decimal", value: (i: any) => i.unitPrice ?? null },
      { key: "currency", value: (i: any) => i.currency || "" },
      { key: "wastage", type: "decimal", value: (i: any) => i.wastagePercentage ?? null },
      { key: "supplier", value: (i: any) => i.supplier || "" },
      { key: "updated", type: "datetime", value: (i: any) => i.lastPriceUpdate || null },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { ctx: cmCf, filtered: cmItems } = useTableColumnFilters(cmBaseItems, cmItemDefs);

  const kpi = useMemo(() => {
    const used = new Set(costItems.map((i) => i.costCategoryId));
    return {
      emptyCats: categories.filter((c) => !used.has(c.costCategoryId)).length,
      foreign: costItems.filter((i) => i.currency && i.currency !== "TRY").length,
      wastage: costItems.filter((i) => !!i.wastagePercentage).length,
      suppliers: new Set(costItems.map((i) => (i.supplier || "").trim()).filter(Boolean)).size,
    };
  }, [categories, costItems]);

  const filteredCategories = categories.filter(
    (c) => !categorySearch || turkishIncludes(c.categoryName || "", categorySearch) || turkishIncludes(c.description || "", categorySearch),
  );

  if (loading && !loadedOnce) {
    return <PageLoader message="Maliyet verileri yükleniyor..." />;
  }

  const canItemUpdate = has("cost:items:update");
  const canItemDelete = has("cost:items:delete");
  const itemFilterOn = !!(itemSearch || selectedCategoryFilter || cmCf.hasActive);

  const itemActs = (item: CostItem) =>
    (canItemUpdate || canItemDelete) && (
      <td className="act">
        <div className="po-rowacts" onClick={(e) => e.stopPropagation()}>
          {canItemUpdate && (
            <button type="button" className="po-ib" title="Düzenle" aria-label={`${item.itemName} düzenle`} onClick={() => handleEditItem(item)}>{I.edit}</button>
          )}
          {canItemDelete && (
            <button type="button" className="po-ib danger" title="Sil" aria-label={`${item.itemName} sil`} onClick={() => handleDeleteCostItem(item.costItemId, item.itemName)}>{I.trash}</button>
          )}
        </div>
      </td>
    );

  const priceCell = (item: CostItem) => (
    <>
      <span className="po-amt">{formatCurrency(item.unitPrice, item.currency)}</span>
      {item.discountPercentage ? <span className="po-badge warn cm-disc">-%{item.discountPercentage}</span> : null}
    </>
  );

  return (
    <div className="po-page cm-page">
      <header className="po-head">
        <div>
          <span className="po-kicker">Maliyet</span>
          <h1>Maliyet yönetimi</h1>
          <p>Maliyet kategorileri ve kalemleri: birim fiyat, döviz, fire ve tedarikçi. Model maliyet setleri bu tanımlardan hesaplanır.</p>
        </div>
        <div className="po-head-actions">
          {has("cost:categories:create") && (
            <button type="button" className="po-btn" onClick={() => navigate("/cost/categories/new")}>{I.plus} Yeni kategori</button>
          )}
          {has("cost:items:create") && (
            <button type="button" className="po-btn primary" onClick={() => navigate("/cost/items/new")}>{I.plus} Yeni maliyet kalemi</button>
          )}
        </div>
      </header>

      <section className="po-kpis">
        <div className="po-kpi navy">
          <span className="l">Kategori</span>
          <span className="v">{categories.length}</span>
          <span className="s">{kpi.emptyCats ? <><b>{kpi.emptyCats}</b> kategoride kalem yok</> : "hepsinde kalem var"}</span>
        </div>
        <div className="po-kpi">
          <span className="l">Maliyet kalemi</span>
          <span className="v">{costItems.length}</span>
          <span className="s">{kpi.suppliers ? <><b>{kpi.suppliers}</b> farklı tedarikçi</> : "tedarikçi girilmemiş"}</span>
        </div>
        <div className="po-kpi">
          <span className="l">Dövizli kalem</span>
          <span className="v">{kpi.foreign}</span>
          <span className="s">USD / EUR / GBP fiyatlı, TL'ye kurla çevrilir</span>
        </div>
        <div className="po-kpi">
          <span className="l">Fire tanımlı</span>
          <span className="v">{kpi.wastage}</span>
          <span className="s">toplama fire yüzdesi eklenen kalem</span>
        </div>
      </section>

      <div className="po-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={activeTab === "categories"} className={activeTab === "categories" ? "on" : ""} onClick={() => setActiveTab("categories")}>
          Kategoriler <span>{categories.length}</span>
        </button>
        <button type="button" role="tab" aria-selected={activeTab === "items"} className={activeTab === "items" ? "on" : ""} onClick={() => setActiveTab("items")}>
          Maliyet kalemleri <span>{costItems.length}</span>
        </button>
      </div>

      {activeTab === "categories" && (
        <section className={`po-card ${loading ? "dim" : ""}`}>
          <div className="po-toolbar">
            <div className="po-search">
              {I.search}
              <input placeholder="Kategori ara (ad, açıklama)" value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} />
              {categorySearch && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => setCategorySearch("")}>{I.close}</button>}
            </div>
          </div>
          <div className="po-card-head">
            <span><b>{filteredCategories.length}</b> kategori{filteredCategories.length !== categories.length && <> / {categories.length}</>}</span>
            <span className="po-hint po-hide-sm">Satıra tıklayınca kategorinin kalemleri açılır</span>
          </div>
          {filteredCategories.length === 0 ? (
            <div className="po-blank">
              <div className="po-blank-icon">{I.inbox}</div>
              <b>{categories.length === 0 ? "Henüz maliyet kategorisi yok" : "Aramayla eşleşen kategori yok"}</b>
              {categories.length === 0 ? "Fason, baskı, aksesuar gibi kategoriler açıp kalemleri altına ekleyin." : "Aramayı değiştirin ya da temizleyin."}
              {categories.length === 0 && has("cost:categories:create") && (
                <button type="button" className="po-btn primary" onClick={() => navigate("/cost/categories/new")}>{I.plus} İlk kategoriyi oluştur</button>
              )}
            </div>
          ) : (
            <div className="po-table-wrap">
              <table className="po-table cm-cat-table">
                <thead>
                  <tr>
                    <th>Kategori</th>
                    <th className="num">Kalem</th>
                    <th className="act" aria-label="İşlemler" />
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.map((category) => {
                    const categoryItems = getCategoryItems(category.costCategoryId);
                    const isExpanded = expandedCategories.has(category.costCategoryId);

                    return (
                      <React.Fragment key={category.costCategoryId}>
                        <tr className={isExpanded ? "on" : ""} onClick={() => toggleCategory(category.costCategoryId)} aria-expanded={isExpanded}>
                          <td>
                            <div className="cm-cat">
                              <span className={`cm-chev ${isExpanded ? "open" : ""}`} aria-hidden="true">{I.chev}</span>
                              <div>
                                <div className="po-name">{category.categoryName}</div>
                                {category.description && <div className="po-sub">{category.description}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="num">
                            {categoryItems.length ? <b>{categoryItems.length}</b> : <span className="po-badge muted">boş</span>}
                          </td>
                          <td className="act">
                            <div className="po-rowacts" onClick={(e) => e.stopPropagation()}>
                              {has("cost:categories:update") && (
                                <button type="button" className="po-ib" title="Düzenle" aria-label={`${category.categoryName} düzenle`} onClick={() => handleEditCategory(category)}>{I.edit}</button>
                              )}
                              {has("cost:categories:delete") && (
                                <button type="button" className="po-ib danger" title="Sil" aria-label={`${category.categoryName} sil`} onClick={() => handleDeleteCategory(category.costCategoryId, category.categoryName)}>{I.trash}</button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="static cm-sub">
                            <td colSpan={3}>
                              {categoryItems.length === 0 ? (
                                <div className="po-blank small">
                                  <b>Bu kategoriye ait maliyet kalemi yok</b>
                                  {has("cost:items:create") && (
                                    <button type="button" className="po-btn small" onClick={() => navigate("/cost/items/new")}>{I.plus} Kalem ekle</button>
                                  )}
                                </div>
                              ) : (
                                <div className="po-table-wrap tight">
                                  <table className="po-table small">
                                    <thead>
                                      <tr>
                                        <th>Kalem</th>
                                        <th className="num">Birim fiyat</th>
                                        <th className="po-hide-sm">Döviz</th>
                                        <th className="num po-hide-sm">Fire</th>
                                        <th className="po-hide-md">Tedarikçi</th>
                                        {(canItemUpdate || canItemDelete) && <th className="act" aria-label="İşlemler" />}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {categoryItems.map((item) => (
                                        <tr key={item.costItemId} className={canItemUpdate ? "" : "static"} onClick={canItemUpdate ? () => handleEditItem(item) : undefined}>
                                          <td>
                                            <div className="po-name">{item.itemName}</div>
                                            {item.description && <div className="po-sub">{item.description}</div>}
                                          </td>
                                          <td className="num">{priceCell(item)}</td>
                                          <td className="po-hide-sm"><span className="po-badge muted">{item.currency}</span></td>
                                          <td className="num po-hide-sm">{item.wastagePercentage ? <span className="cm-waste">%{item.wastagePercentage}</span> : <span className="cm-nil">—</span>}</td>
                                          <td className="po-hide-md">{item.supplier || <span className="cm-nil">—</span>}</td>
                                          {itemActs(item)}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {activeTab === "items" && (
        <section className={`po-card cm-items ${loading ? "dim" : ""}`}>
          <div className="po-toolbar">
            <div className="po-search">
              {I.search}
              <input placeholder="Kalem ara (ad, açıklama, tedarikçi)" value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} />
              {itemSearch && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => setItemSearch("")}>{I.close}</button>}
            </div>
            <select className="po-select cm-catsel" aria-label="Kategori" value={selectedCategoryFilter} onChange={(e) => setSelectedCategoryFilter(e.target.value)}>
              <option value="">Tüm kategoriler</option>
              {categories.map((cat) => (
                <option key={cat.costCategoryId} value={cat.costCategoryId}>
                  {cat.categoryName}
                </option>
              ))}
            </select>
            {itemFilterOn && (
              <button type="button" className="po-clear" onClick={() => { setItemSearch(""); setSelectedCategoryFilter(""); cmCf.clearAll(); }}>{I.filterOff} Temizle</button>
            )}
          </div>
          <div className="po-card-head">
            <span><b>{cmItems.length}</b> kalem{cmItems.length !== costItems.length && <> / {costItems.length}</>}</span>
            <span className="po-hint po-hide-sm">{canItemUpdate ? "Satıra tıklayınca hızlı düzenleme açılır · başlıklardaki süzgeçler sütun bazında süzer" : "Başlıklardaki süzgeçler sütun bazında süzer"}</span>
          </div>
          <div className="po-table-wrap">
            <table className="po-table cm-item-table">
              <thead>
                <tr>
                  <FilterTh label="Kalem adı" colKey="name" ctx={cmCf} />
                  <FilterTh label="Kategori" colKey="category" ctx={cmCf} className="po-hide-sm" />
                  <FilterTh label="Birim fiyat" colKey="price" type="decimal" ctx={cmCf} align="left" className="num" />
                  <FilterTh label="Döviz" colKey="currency" ctx={cmCf} className="po-hide-sm" />
                  <FilterTh label="Fire %" colKey="wastage" type="decimal" ctx={cmCf} align="left" className="num po-hide-sm" />
                  <FilterTh label="Tedarikçi" colKey="supplier" ctx={cmCf} className="po-hide-md" />
                  <FilterTh label="Son güncelleme" colKey="updated" type="datetime" ctx={cmCf} className="po-hide-lg" />
                  {(canItemUpdate || canItemDelete) && <th className="act" aria-label="İşlemler" />}
                </tr>
              </thead>
              <tbody>
                {cmItems.length === 0 ? (
                  <tr className="static">
                    <td colSpan={8}>
                      <div className="po-blank">
                        <div className="po-blank-icon">{I.inbox}</div>
                        <b>{costItems.length === 0 ? "Henüz maliyet kalemi yok" : "Süzgeçle eşleşen kalem yok"}</b>
                        {costItems.length === 0 ? "Bir kategori seçip fiyatı, dövizi ve fire oranıyla kalem tanımlayın." : "Arama, kategori ya da sütun süzgeçlerini değiştirin."}
                      </div>
                    </td>
                  </tr>
                ) : (
                  cmItems.map((item) => (
                    <tr key={item.costItemId} className={canItemUpdate ? "" : "static"} onClick={canItemUpdate ? () => handleEditItem(item) : undefined}>
                      <td>
                        <div className="po-name">{item.itemName}</div>
                        <div className="po-sub">
                          {item.description || ""}
                          <span className="po-only-sm">{item.description ? " · " : ""}{item.costCategory?.categoryName || getCategoryName(item.costCategoryId)}</span>
                        </div>
                      </td>
                      <td className="po-hide-sm">
                        <span className="cm-cat-pill">{item.costCategory?.categoryName || getCategoryName(item.costCategoryId)}</span>
                      </td>
                      <td className="num">{priceCell(item)}</td>
                      <td className="po-hide-sm"><span className="po-badge muted">{item.currency}</span></td>
                      <td className="num po-hide-sm">{item.wastagePercentage ? <span className="cm-waste">%{item.wastagePercentage}</span> : <span className="cm-nil">—</span>}</td>
                      <td className="po-hide-md">{item.supplier || <span className="cm-nil">—</span>}</td>
                      <td className="po-hide-lg cm-date">{formatDate(item.lastPriceUpdate)}</td>
                      {itemActs(item)}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Edit Modal */}
      {editModalOpen && editingItem && (
        <EditCostItemModal
          item={editingItem}
          categories={categories}
          onClose={() => {
            setEditModalOpen(false);
            setEditingItem(null);
          }}
          onOpenDetail={() => navigate(`/cost/items/${editingItem.costItemId}/edit`)}
          onSave={handleSaveEdit}
        />
      )}

      {/* Edit Category Modal */}
      {editCategoryModalOpen && editingCategory && (
        <EditCategoryModal
          category={editingCategory}
          onClose={() => {
            setEditCategoryModalOpen(false);
            setEditingCategory(null);
          }}
          onSave={handleSaveCategoryEdit}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Sil"
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

interface EditCostItemModalProps {
  item: CostItem;
  categories: CostCategory[];
  onClose: () => void;
  onOpenDetail: () => void;
  onSave: (item: CostItem) => void;
}

const EditCostItemModal: React.FC<EditCostItemModalProps> = ({
  item,
  categories,
  onClose,
  onOpenDetail,
  onSave,
}) => {
  const { hasModule } = usePermissions();
  const [formData, setFormData] = useState<CostItem>({ ...item });
  useEscClose(onClose);
  // Stok bağı bu kısa formdan kaldırıldı (kalem detay ekranında, çok satırlı);
  // eskiden burada stok listesi yükleniyordu, artık gerek yok.

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="po-modal wide" role="dialog" aria-modal="true" aria-labelledby="cm-item-title" onSubmit={handleSubmit}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">Maliyet kalemi</span>
            <h3 id="cm-item-title">{item.itemName || "Maliyet kalemini düzenle"}</h3>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </div>

        <div className="po-modal-body">
          <div className="po-form-sec">
            <h4>Kalem</h4>
            <div className="po-grid3">
              <label className="po-field span3">
                <span>Kalem adı <em className="err">zorunlu</em></span>
                <input
                  type="text"
                  value={formData.itemName}
                  onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                  required
                  autoFocus
                />
              </label>
              <label className="po-field">
                <span>Kategori <em className="err">zorunlu</em></span>
                <select
                  value={formData.costCategoryId}
                  onChange={(e) => setFormData({ ...formData, costCategoryId: e.target.value })}
                  required
                >
                  <option value="">Seçin</option>
                  {categories.map((cat) => (
                    <option key={cat.costCategoryId} value={cat.costCategoryId}>
                      {cat.categoryName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="po-field">
                <span>Birim fiyat <em className="err">zorunlu</em></span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.unitPrice}
                  onChange={(e) => setFormData({ ...formData, unitPrice: parseFloat(e.target.value) })}
                  required
                />
              </label>
              <label className="po-field">
                <span>Döviz <em className="err">zorunlu</em></span>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  required
                >
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </label>
              <label className="po-field span2">
                <span>Tedarikçi</span>
                <input
                  type="text"
                  value={formData.supplier || ""}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                />
              </label>
              <label className="po-field">
                <span>Fire <em>%</em></span>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={formData.wastagePercentage || ""}
                  onChange={(e) => setFormData({ ...formData, wastagePercentage: parseFloat(e.target.value) || undefined })}
                />
              </label>
              <label className="po-field span3">
                <span>Açıklama</span>
                <textarea
                  value={formData.description || ""}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </label>
            </div>
          </div>

          <div className="po-form-sec">
            <h4>Miktar etiketleri</h4>
            <div className="po-grid2">
              <label className="po-field">
                <span>1. miktar etiketi</span>
                <input
                  type="text"
                  value={formData.quantity1Label || ""}
                  onChange={(e) => setFormData({ ...formData, quantity1Label: e.target.value })}
                  placeholder="Örn: En, Miktar, Adet"
                />
              </label>
              {formData.quantity2Label && (
                <label className="po-field">
                  <span>2. miktar etiketi</span>
                  <input
                    type="text"
                    value={formData.quantity2Label || ""}
                    onChange={(e) => setFormData({ ...formData, quantity2Label: e.target.value })}
                    placeholder="Örn: Boy, Çeşit sayısı"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Stok düşümü artık çok satırlı (bir kalem birden fazla malzeme tüketebilir).
              Bu kısa formda tekrar etmek yerine kalem detay ekranına yönlendiriyoruz. */}
          {hasModule("stock") && (
            <div className="po-note info">
              <b>Stok düşümü</b> kalem detay ekranından tanımlanır; bir kaleme birden fazla malzeme bağlanabilir.{" "}
              <button type="button" className="po-link" onClick={onOpenDetail}>Kalem detayını aç</button>
            </div>
          )}
        </div>

        <div className="po-modal-foot">
          <button type="button" className="po-btn" onClick={onClose}>Vazgeç</button>
          <button type="submit" className="po-btn primary">Kaydet</button>
        </div>
      </form>
    </div>
  );
};

interface EditCategoryModalProps {
  category: CostCategory;
  onClose: () => void;
  onSave: (category: CostCategory) => void;
}

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({
  category,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<CostCategory>({ ...category });
  useEscClose(onClose);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="po-modal" role="dialog" aria-modal="true" aria-labelledby="cm-cat-title" onSubmit={handleSubmit}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">Maliyet kategorisi</span>
            <h3 id="cm-cat-title">Kategoriyi düzenle</h3>
          </div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </div>

        <div className="po-modal-body">
          <label className="po-field">
            <span>Kategori adı <em className="err">zorunlu</em></span>
            <input
              type="text"
              value={formData.categoryName}
              onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
              required
              autoFocus
            />
          </label>
          <label className="po-field">
            <span>Açıklama</span>
            <textarea
              value={formData.description || ""}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </label>
        </div>

        <div className="po-modal-foot">
          <button type="button" className="po-btn" onClick={onClose}>Vazgeç</button>
          <button type="submit" className="po-btn primary">Kaydet</button>
        </div>
      </form>
    </div>
  );
};

export default CostManagement;
